/**
 * `/incidents` — WP-L5, Task 53. `web/shapes/incidents.md` is the agreed shape.
 *
 * The screen the acceptance criterion lives on: *"UI không cho transition không hợp
 * lệ."* `TransitionControls` is where that is enforced and its header is where the
 * argument is; this file's job is to hold the two reads, the two writes and the one
 * rule about what each refusal does to what is on screen.
 *
 * ── Two reads, deliberately separate ────────────────────────────────────────
 *
 * The queue (`GET /incidents`, offset-paged, a real `total`) and the detail
 * (`GET /incidents/{id}`, which carries `comments` and — the reason it must be its
 * own request — the **`ETag`**). A detail lifted out of the list page would carry no
 * precondition, and every `PATCH` from it would be refused `400` for a missing
 * `If-Match`. Re-reading also means the detail answers for the incident as it is
 * **now** rather than as it was when the page was fetched, which is the case that
 * matters on a queue two people work.
 *
 * ── What each refusal does to the screen ────────────────────────────────────
 *
 * All three: **the server's own sentence, rendered; the prior state, left standing.**
 *
 *   read refused        → `ApiErrorNote` in that region; the other region keeps its data
 *   transition refused  → the sentence plus the server's `allowed_transitions[]`, the
 *                         incident unchanged, **and a re-read**, because in this one
 *                         case something did change: the state the screen was drawn
 *                         from is wrong
 *   patch refused       → the sentence, the operator's unsaved edit kept, and a
 *                         re-read so the next attempt carries a live `ETag`. Never a
 *                         silent retry with `details.current_etag`
 *
 * Nothing is applied optimistically anywhere here, so there is no rollback to get
 * wrong — the same choice `/policies` made and for the same reason.
 *
 * ── There is no delete, and the screen says why ─────────────────────────────
 *
 * `openapi.yaml` publishes no `DELETE /incidents/{id}`, and the database refuses one
 * for **every** severity (`WORKFORCE_INCIDENT_UNDELETABLE`, SQLSTATE 23001) — a guard
 * that only checked `critical` would be bypassed by a downgrade followed by a delete.
 * So there is no delete control and no disabled one; the panel that would have
 * carried it says what actually happens to an incident that should not have been
 * opened, because an operator who believes a delete exists goes looking for one.
 */

import { Fragment, useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { formatInstant } from '../i18n/datetime';
import { useSession } from '../auth/SessionProvider';
import { incidentPath, incidentTransitionPath, incidentsPath } from '../api/paths';
import type {
  Incident,
  IncidentListResponse,
  IncidentPatchRequest,
  IncidentTransitionRequest,
} from '../api/types';
import { useApiMutation } from '../api/useApiMutation';
import { useApiQuery } from '../api/useApiQuery';
import { accessHeaders } from '../access/reason';
import { AccessReasonGate } from '../access/AccessReasonGate';
import { BADGE_SEVERITY } from '../alerts/inbox';
import { INCIDENT_STATES, isIncidentState } from '../incidents/state-machine';
import { SEVERITY_LABEL, STATE_BADGE, STATE_LABEL } from '../incidents/labels';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Badge } from '../states/Badge';
import { EmptyState } from '../states/EmptyState';
import { PartialData } from '../states/PartialData';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import { SEVERITY_ICON } from '../states/icons';
import { ScreenHeading } from '../shell/ScreenHeading';
import { AssignmentPanel } from './incidents/AssignmentPanel';
import { EvidencePanel, Timeline } from './incidents/EvidencePanel';
import { SlaPanel } from './incidents/SlaPanel';
import { TransitionControls } from './incidents/TransitionControls';

const PAGE_LIMIT = 50;

export function IncidentsScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const [reason, setReason] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [listToken, setListToken] = useState(0);
  const [detailToken, setDetailToken] = useState(0);

  const timezone = user?.timezone ?? 'Asia/Ho_Chi_Minh';
  const headers = accessHeaders(reason);
  const headerOption = headers === undefined ? {} : { headers };

  const listPath =
    headers === undefined
      ? null
      : incidentsPath({
          limit: PAGE_LIMIT,
          state: stateFilter === '' ? undefined : stateFilter,
        });
  const list = useApiQuery<IncidentListResponse>(listPath, listToken, headerOption);

  const detailPath = headers === undefined || selectedId === null ? null : incidentPath(selectedId);
  const detail = useApiQuery<Incident>(detailPath, detailToken, headerOption);

  const move = useApiMutation<IncidentTransitionRequest, Incident>(
    incidentTransitionPath('unset'),
    'POST',
    headers ?? {},
  );
  const patch = useApiMutation<IncidentPatchRequest, Incident>(
    incidentPath('unset'),
    'PATCH',
    headers ?? {},
  );

  const rows: readonly Incident[] = useMemo(() => list.data?.data ?? [], [list.data]);
  const total = list.data?.page.total ?? rows.length;

  /**
   * The incident the panels are drawn from, and its precondition.
   *
   * Both come from the **detail** read: `detail.data` for the body and `detail.etag`
   * for the token. A write's own response also carries a fresh `ETag`, so a second
   * `PATCH` in a row does not need a re-read — that is what `patch.etag` is for, and
   * it takes precedence when it exists.
   */
  const current = detail.data;
  const etag = patch.etag ?? detail.etag;

  const settle = (): void => {
    // Both writes re-read the detail: a transition changes the state the controls are
    // derived from, and a refusal means the state on screen is stale. Both also
    // re-read the list, because the row's badge and its SLA column moved with it.
    setDetailToken((value) => value + 1);
    setListToken((value) => value + 1);
  };

  const submitMove = (body: IncidentTransitionRequest): void => {
    if (selectedId === null) return;
    move
      .submit(body, incidentTransitionPath(selectedId))
      .then(settle)
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  const submitPatch = (body: IncidentPatchRequest): void => {
    if (selectedId === null || etag === null) return;
    patch
      // The precondition, verbatim from the header of the read that produced this
      // incident. Quoted here because `If-Match` is an entity-tag header and the
      // server strips the quoting; the value between the quotes is untouched.
      .submit(body, incidentPath(selectedId), { 'If-Match': `"${etag}"` })
      .then(settle)
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  /**
   * The detail region, rendered **inside the queue** under the row it describes.
   *
   * It is built here rather than inside the map so that the list stays a list: one
   * expression, rendered at most once, in the one place `selectedId` names.
   */
  const detailRegion = (
    <>
      {detail.status === 'loading' ? (
        <Skeleton
          rows={5}
          variant="block"
          label={t('incidents.detail.subject')}
          testId="l5-detail-skeleton"
        />
      ) : null}
      {detail.status === 'error' && detail.error !== null ? (
        <ApiErrorNote
          error={detail.error}
          onRetry={detail.reload}
          subject={t('incidents.detail.subject')}
          testId="l5-detail-error"
        />
      ) : null}

      {current === null ? null : (
        <section className="wp-section" aria-labelledby="l5-detail-heading" data-testid="l5-detail">
          <h2 className="wp-section__title" id="l5-detail-heading" tabIndex={-1}>
            {t('incidents.detail.title', {
              number: current.incident_number,
              title: current.title,
            })}
          </h2>

          <SlaPanel
            slaDueAt={current.sla_due_at}
            slaBreached={current.sla_breached}
            state={isIncidentState(current.state) ? current.state : 'new'}
            timezone={timezone}
            nowMs={Date.now()}
          />

          {isIncidentState(current.state) ? (
            <TransitionControls
              state={current.state}
              pending={move.status === 'pending'}
              error={move.error}
              onSubmit={submitMove}
            />
          ) : (
            // A state this build has no table row for. Rendering *some*
            // buttons would be guessing at a machine this client does not
            // know; rendering none and saying so is the fail-closed direction.
            <StatusNote
              severity="high"
              role="alert"
              title={t('incidents.unknownState.title')}
              testId="l5-unknown-state"
            >
              <p>{t('incidents.unknownState.body', { state: current.state })}</p>
            </StatusNote>
          )}

          {move.status === 'ok' && move.data !== null ? (
            <StatusNote
              severity="low"
              title={t('incidents.transition.ok', {
                state: isIncidentState(move.data.state)
                  ? t(STATE_LABEL[move.data.state])
                  : move.data.state,
              })}
              testId="l5-transition-ok"
            />
          ) : null}

          <AssignmentPanel
            key={`${current.id}:${current.updated_at}`}
            ownerId={current.owner_id}
            severity={current.severity}
            etag={etag}
            pending={patch.status === 'pending'}
            error={patch.error}
            saved={patch.status === 'ok'}
            onSubmit={submitPatch}
          />

          <EvidencePanel
            evidence={current.evidence}
            redacted={current.redacted}
            timezone={timezone}
          />

          <Timeline comments={current.comments} timezone={timezone} />

          {/* Named on the screen, not only in a report. */}
          <StatusNote severity="info" title={t('incidents.noDelete.title')} testId="l5-no-delete">
            <p>{t('incidents.noDelete.body')}</p>
            <p>{t('incidents.noDelete.instead')}</p>
          </StatusNote>
        </section>
      )}
    </>
  );

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('nav.incidents')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('incidents.intro')}</p>

      <AccessReasonGate
        value={reason}
        subject={t('incidents.subjectWord')}
        testId="l5-incidents-access"
        onOpen={(next) => {
          setReason(next);
          setSelectedId(null);
          move.reset();
          patch.reset();
        }}
      />

      {reason === '' ? (
        <StatusNote severity="info" title={t('access.closed.title')} testId="l5-incidents-closed">
          <p>{t('access.closed.body', { subject: t('incidents.subjectWord') })}</p>
        </StatusNote>
      ) : (
        <>
          {/* The scope sentence before the control that narrows it: what this
              operator can ever see is not the same statement as what they have
              filtered to, and printing it under the select reads as a caption on
              the select. */}
          <p className="wp-small wp-muted" data-testid="l5-incidents-scope">
            {t('incidents.scopeNote')}
          </p>

          <div className="wp-c-bar">
            <div className="wp-c-bar__group">
              <label className="wp-c-bar__legend" htmlFor="l5-incident-state">
                {t('incidents.filter.state')}
              </label>
              <select
                className="wp-field__input"
                id="l5-incident-state"
                data-testid="l5-incident-state-filter"
                value={stateFilter}
                onChange={(event) => {
                  setStateFilter(event.target.value);
                  setSelectedId(null);
                }}
              >
                <option value="">{t('filters.all')}</option>
                {INCIDENT_STATES.map((state) => (
                  <option key={state} value={state}>
                    {t(STATE_LABEL[state])}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {list.status === 'loading' ? (
            <Skeleton
              rows={4}
              variant="block"
              label={t('incidents.subjectWord')}
              testId="l5-incidents-skeleton"
            />
          ) : null}
          {list.status === 'error' && list.error !== null ? (
            <ApiErrorNote
              error={list.error}
              onRetry={list.reload}
              subject={t('incidents.subjectWord')}
              testId="l5-incidents-error"
            />
          ) : null}

          {list.data === null ? null : rows.length === 0 ? (
            <EmptyState
              title={t('incidents.empty.title')}
              description={t('incidents.empty.body', {
                state: stateFilter === '' ? t('filters.all') : stateFilter,
              })}
              testId="l5-incidents-empty"
            />
          ) : (
            <>
              {rows.length < total ? (
                <PartialData
                  excludes={t('incidents.partial.excludes', {
                    shown: rows.length,
                    total,
                  })}
                  testId="l5-incidents-partial"
                />
              ) : null}
              <ul className="wp-c-ruled" data-testid="l5-incident-list">
                {rows.map((incident) => (
                  <Fragment key={incident.id}>
                    <li
                      className={`wp-c-ruled__row${
                        // A tinted row is one that needs somebody today: the deadline
                        // has been missed. Wash plus 1px on all four sides, never an
                        // edge stripe (DESIGN.md §12).
                        incident.sla_breached ? ' wp-c-ruled__row--act-critical' : ''
                      }${selectedId === incident.id ? ' wp-c-ruled__row--open' : ''}`}
                      data-testid={`l5-incident-${incident.id}`}
                    >
                      <div className="wp-c-ruled__main">
                        <p className="wp-c-ruled__line">
                          <Badge
                            severity={BADGE_SEVERITY[incident.severity]}
                            icon={SEVERITY_ICON[BADGE_SEVERITY[incident.severity]]}
                            label={t(SEVERITY_LABEL[incident.severity])}
                            testId={`l5-incident-severity-${incident.id}`}
                          />{' '}
                          <Badge
                            severity={
                              isIncidentState(incident.state) ? STATE_BADGE[incident.state] : 'info'
                            }
                            icon={
                              SEVERITY_ICON[
                                isIncidentState(incident.state)
                                  ? STATE_BADGE[incident.state]
                                  : 'info'
                              ]
                            }
                            label={
                              isIncidentState(incident.state)
                                ? t(STATE_LABEL[incident.state])
                                : incident.state
                            }
                            testId={`l5-incident-state-${incident.id}`}
                          />
                          <span>{incident.title}</span>
                        </p>
                        {/* The number and the deadline on one printed meta line: two
                          facts a reader compares down the column, so both are
                          tabular. */}
                        <p className="wp-c-ruled__meta">
                          <span className="wp-mono">#{incident.incident_number}</span>
                          {' · '}
                          {incident.sla_breached
                            ? t('incidents.row.breached')
                            : incident.sla_due_at === null
                              ? t('incidents.row.noSla')
                              : t('incidents.row.due', {
                                  when: formatInstant(incident.sla_due_at, locale, timezone),
                                })}
                        </p>
                      </div>
                      <div className="wp-c-ruled__aside">
                        <button
                          className="wp-button"
                          type="button"
                          aria-expanded={selectedId === incident.id}
                          data-testid={`l5-open-${incident.id}`}
                          onClick={() => {
                            move.reset();
                            patch.reset();
                            setSelectedId(incident.id);
                          }}
                        >
                          {t('incidents.open')}
                        </button>
                      </div>
                    </li>
                    {/* The incident unfolds **under its own row**, inside the queue,
                      rather than as a second page below fifty rows. The state
                      machine, the deadline and the evidence are all read against the
                      row they belong to, and nothing here needs protected focus, so
                      nothing here is a modal. */}
                    {selectedId === incident.id ? (
                      <li className="wp-c-ruled__drawer">{detailRegion}</li>
                    ) : null}
                  </Fragment>
                ))}
              </ul>

              {/* The one case the drawer cannot cover: the chosen incident is no
                  longer on the page the filter returned — a transition moved it out
                  of the state being listed. The detail (and the transition's own
                  confirmation) stays on screen below the queue rather than vanishing
                  at the moment it says the write succeeded. */}
              {selectedId !== null && !rows.some((one) => one.id === selectedId)
                ? detailRegion
                : null}
            </>
          )}
        </>
      )}
    </div>
  );
}
